/**
 * Loading and error blocks.
 *
 * Pages use these instead of inventing their own spinners, which keeps the
 * waiting states visually consistent across every level of the app.
 */

import { Loader2 } from 'lucide-react'

import { cn } from '../../lib/format'
import { Alert, AlertDescription, AlertTitle } from '../ui/alert'
import { Button } from '../ui/button'
import { Skeleton } from '../ui/primitives'

export function LoadingBlock({ label, className }: { label: string; className?: string }) {
  return (
    <div className={cn('text-muted-foreground flex items-center gap-2 py-6 text-sm', className)}>
      <Loader2 className="size-4 animate-spin" aria-hidden />
      <span>{label}</span>
    </div>
  )
}

export function SkeletonCard({ className }: { className?: string }) {
  return (
    <div className={cn('border-border flex flex-col gap-3 rounded-xl border p-4', className)}>
      <Skeleton className="h-4 w-1/3" />
      <Skeleton className="h-3 w-2/3" />
      <Skeleton className="h-3 w-1/2" />
    </div>
  )
}

export interface ErrorBlockProps {
  title: string
  description?: string
  onRetry?: () => void
  retryLabel: string
  className?: string
}

/**
 * Failure notice that carries a message and nothing else.
 *
 * It is the same alert `ErrorBlock` builds, minus the title and retry that block
 * requires, and it renders nothing when there is no message, so a page can hand
 * it an optional string straight from a mutation.
 */
export function ErrorAlert({ message }: { message: string | null | undefined }) {
  if (!message) {
    return null
  }

  return (
    <Alert variant="destructive">
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  )
}

export function ErrorBlock({
  title,
  description,
  onRetry,
  retryLabel,
  className,
}: ErrorBlockProps) {
  return (
    <Alert variant="destructive" className={className}>
      <AlertTitle>{title}</AlertTitle>
      {description ? <AlertDescription>{description}</AlertDescription> : null}
      {onRetry ? (
        <div className="pt-1">
          <Button variant="outline" size="sm" onClick={onRetry}>
            {retryLabel}
          </Button>
        </div>
      ) : null}
    </Alert>
  )
}
